import { defineStore } from 'pinia';
import { useUser } from './user';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { tagOrderIndex, sortByTagOrder } from '@lib/tag-order';
import { computed, type ComputedRef } from 'vue';
import type { PlacementTagCategoryDto, PlacementTagDto } from '@/ApiClient';

export const usePlacementTags = defineStore('placement-tags', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.placementTag_GetPlacementTagCategories,
    dto: 'PlacementTagCategoryDto',
    create: api.placementTag_CreatePlacementTagCategory,
    delete: api.placementTag_DeletePlacementTagCategory,
    update: api.placementTag_UpdatePlacementTagCategory,
    signalR: 'PlacementTagUpdate',
  });
  const { loaded } = globalStoreBase;

  const any: ComputedRef<boolean> = computed(() =>
    Boolean(loaded.value.length && loaded.value.some((c) => c.tags && c.tags.length > 0))
  );

  const sorted: ComputedRef<PlacementTagCategoryDto[]> = computed(() =>
    loaded.value
      .slice()
      .sort((a, b) => a.name?.localeCompare(b.name || '') || 0)
      .map((c) => ({
        ...c,
        tags: c.tags?.slice().sort((a, b) => a.name?.localeCompare(b.name || '') || 0),
      }))
  );

  const flattened: ComputedRef<PlacementTagDto[]> = computed(() => loaded.value.flatMap((c) => c.tags || []));

  const baseCategory = computed(() => loaded.value[0]);

  async function addNewTag(name: string, color: string, tagCategoryId: number) {
    const brandId = useUser().brandId;
    const tag = await api.placementTag_CreatePlacementTag(brandId, {
      name,
      color,
      tagCategoryId,
      isOncePerBom: false,
    });

    const category = loaded.value.find((c) => c.id === tagCategoryId);
    if (category) {
      if (!category.tags) category.tags = [];
      category.tags.push(tag);
    }
    return tag;
  }

  async function updateTag(tagId: number, name: string, color: string, isOncePerBom: boolean) {
    const brandId = useUser().brandId;
    const tag = await api.placementTag_UpdatePlacementTag(brandId, tagId, { name, color, isOncePerBom });

    const category = loaded.value.find((c) => c.id === tag.tagCategoryId);
    if (category) {
      const idx = category.tags?.findIndex((t) => t.id === tagId);
      if (idx !== undefined && idx >= 0) category.tags!.splice(idx, 1, tag);
    }
    return tag;
  }

  async function deleteTag(tagId: number) {
    const brandId = useUser().brandId;
    await api.placementTag_DeletePlacementTag(brandId, tagId);

    const category = loaded.value.find((c) => c.tags?.some((t) => t.id === tagId));
    if (category) {
      const idx = category.tags!.findIndex((t) => t.id === tagId);
      category.tags!.splice(idx, 1);
    }
  }

  function getTagById(id: number): PlacementTagDto | undefined {
    return flattened.value.find((t) => t.id === id);
  }

  function idsToTags(ids: number[]): PlacementTagDto[] {
    return ids.map(getTagById).filter((t): t is PlacementTagDto => !!t);
  }

  function getCategoryById(id: number): PlacementTagCategoryDto | undefined {
    return loaded.value.find((c) => c.id === id);
  }

  // One palette-order Map per store: pill components sort tag ids through this instead of every
  // cell instance building its own Map over the whole palette.
  const orderIndexById: ComputedRef<Map<number, number>> = computed(() => tagOrderIndex(sorted.value));

  function sortByOrder<T extends { id: number }>(tags: T[]): T[] {
    return sortByTagOrder(tags, orderIndexById.value);
  }

  return {
    ...globalStoreBase,
    any,
    sorted,
    orderIndexById,
    sortByOrder,
    flattened,
    baseCategory,
    addNewCategory: globalStoreBase.create,
    updateCategory: globalStoreBase.update,
    deleteCategory: globalStoreBase.delete,
    addNewTag,
    updateTag,
    deleteTag,
    idsToTags,
    getTagById,
    getCategoryById,
  };
});
